import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = "Privacy Policy - IP Partners Inc";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-brown-100 to-brown-50 pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-brown-700 hover:text-brown-800 font-semibold mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-brown-900">Privacy Policy</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-brown max-w-none space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Introduction</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                IP Partners Inc ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you visit our website or 
                use our legal services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Information We Collect</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                <strong>Personal Information:</strong> We may collect personal information that you voluntarily provide 
                to us, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>Name, email address, phone number, and mailing address</li>
                <li>Information about your legal matter or case</li>
                <li>Financial information for billing purposes</li>
                <li>Correspondence and communications with our firm</li>
              </ul>
              <p className="text-brown-700 leading-relaxed mb-4">
                <strong>Automatically Collected Information:</strong> When you visit our website, we may automatically 
                collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>IP address, browser type, and operating system</li>
                <li>Pages visited, time spent on pages, and links clicked</li>
                <li>Referring website or source</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">How We Use Your Information</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>To provide legal services and respond to your inquiries</li>
                <li>To communicate with you about your case or legal matter</li>
                <li>To process payments and maintain billing records</li>
                <li>To improve our website and services</li>
                <li>To send you updates, newsletters, or marketing materials (with your consent)</li>
                <li>To comply with legal obligations and professional responsibilities</li>
                <li>To protect our rights and prevent fraud or illegal activity</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Information Sharing and Disclosure</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your 
                information in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li><strong>Legal Representation:</strong> With other attorneys or staff within our firm as necessary to provide legal services</li>
                <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website or business</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
                <li><strong>Professional Obligations:</strong> As necessary to comply with attorney-client privilege and ethical duties</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Attorney-Client Confidentiality</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Once an attorney-client relationship is established, your communications and information are protected 
                by attorney-client privilege and professional confidentiality rules. We take our ethical obligations 
                seriously and implement appropriate measures to protect privileged information.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Data Security</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                We implement reasonable security measures to protect your information from unauthorized access, 
                alteration, disclosure, or destruction. However, no method of transmission over the internet or 
                electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee 
                its absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Our website uses cookies and similar technologies to enhance your browsing experience, analyze website 
                traffic, and personalize content. You can control cookie settings through your browser preferences. 
                However, disabling cookies may limit your ability to use certain features of our website.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Your Rights and Choices</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-brown-700 space-y-2 mb-4 ml-4">
                <li>Access, correct, or update your personal information</li>
                <li>Request deletion of your information (subject to legal and professional obligations)</li>
                <li>Opt-out of marketing communications</li>
                <li>Disable cookies through your browser settings</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Third-Party Links</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices 
                or content of these external sites. We encourage you to review the privacy policies of any third-party 
                websites you visit.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Children's Privacy</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                Our website and services are not directed to individuals under the age of 18. We do not knowingly 
                collect personal information from children. If we become aware that we have collected information 
                from a child, we will take steps to delete it.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal 
                requirements. We will notify you of any material changes by posting the updated policy on our website.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-brown-900 mb-4">Contact Us</h2>
              <p className="text-brown-700 leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-brown-50 p-6 rounded-lg">
                <p className="text-brown-700 mb-2"><strong>IP Partners Inc</strong></p>
                <p className="text-brown-700 mb-2">1990 N California Blvd, Walnut Creek, CA 94596, United States</p>
                <p className="text-brown-700 mb-2">Phone: +1 (833) 201-7574</p>
                <p className="text-brown-700">Email: info@ippartnerinc.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
